<template>
  <AppView
    :is-mocked-mode="isMockedMode"
    :show-customisation-loader="showCustomisationLoader"
    :show-float-button="showFloatButton"
    :show-my-request="showMyRequest"
    :show-article="showArticle"
    :show-article-id="showArticleId"
    :article-title="articleTitle"
    :category-name="categoryName"
    :article-body="articleBody"
    :article-section-title="articleSectionTitle"
    :fetch-article="fetchArticle"
    :is-from-autocomplete="isFromAutocomplete"
    :article-use-config="articleUseConfig"
    :show-router-view="showRouterView"
    :show-chat-widget="showChatWidget"
    @handle-customisation-retry="handleCustomisationRetry"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { registerEventFromApp, ParentEvDataOpenRes } from '@/common/bridge/legacy-bridge';
import { platform, Bridge } from '@/common/bridge';
import { onOpenMyRequest, onOpenArticle, onFloatButton, emitFloatButton } from '@/common/eventBus';
import AppView from '@/views/AppView';
import { Country, CustomerServiceProvider, MINIMIZE_TIME } from '@/common/config';
import { InitializeOptions, LoginResult, SelectedEntity, CustomisationUpdatePayload } from './common/types';
import { ConnectToAgent } from './common/bot/types';
import { store as vuexStore } from './store';
import * as env from '@/common/environment';
import { checkCustomisationABTestingPassed, isNewChatbotFlowBypassed } from '@/common/abTesting';
import { analytics } from './common/logs/EventLogger';
import { GENERAL_EVENTS } from './common/logs/events';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import { getEntryLog, OpenHelpEntryType } from './common/entryLog';
import { logErrorEvents } from './common/api/api.pleased';
import { useApplyTheme } from '@/common/customisation/composables';
import { useLoadCustomisation } from '@/common/customisation/composables/useLoadCustomisation';
import { useCustomisationStore } from '@/common/customisation/stores/customisation.store';
import { storeToRefs } from 'pinia';
import { useBotUser } from './stores/useBotUser';
import router from './router';
import { useRoute } from 'vue-router/composables';
import { useConfig } from './stores/useConfig';
import { useWidgetConversation } from './stores/useWidgetConversation';
import { useMockedFlow } from './library/cf-v1/mocked-flow';
import { PleasedClient } from './pleased-sdk/PleasedClient';
import { SupportedLanguages } from './locales/languages';
import { isValidSelectedEntity } from './store/user';
import { logger, logWidgetInitEvent } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import {
  captureEventException,
  ErrorTrackingEvent,
  resolveTrackingRoute,
  setChatbotContext,
} from '@/common/error-tracking';

// State
const showFloatButton = ref(false);
const showMyRequest = ref(false);
const fetchArticle = ref(false);
const isFromAutocomplete = ref(false);
const articleTitle = ref('');
const categoryName = ref('');
const showArticle = ref(false);
const showArticleId = ref<string | number>('');
const articleBody = ref('');
const articleSectionTitle = ref('');
const isInitialized = ref(false);
const showRouterView = ref(false);
const articleUseConfig = ref<null | ConnectToAgent>(null);
const isAwaitingJweResolution = ref(false);

// Customisation support
const customisationStore = useCustomisationStore();
// Initialize the composable with false, will enable after isInitialized becomes true
const isCustomisationEnabled = ref(false);
const { isLoading: isLoadingCustomisation, refetch } = useLoadCustomisation(isCustomisationEnabled);

const userStore = useBotUser();
const { isAuthenticated } = storeToRefs(userStore);
const { loginViaToken } = userStore;

const configStore = useConfig();
const mockedFlow = useMockedFlow();
const widgetConversation = useWidgetConversation();

const route = useRoute();

// Initialize theme application - this sets up watchers that will automatically
// apply theme when activeTheme changes (including when documents are loaded), from customisationStore
useApplyTheme();

/**
 * Check if chatbot is in mocked/preview mode.
 * When true, all interactions are blocked via an overlay.
 */
const isMockedMode = computed<boolean>(() => {
  return Boolean(configStore.config?.initialRegistryId);
});

// Computed properties
const pageSource = computed<OpenHelpEntryType>(() => vuexStore.state.user.pageSource);

const isLocalStorageCustomisationEnabled = computed<boolean>(() => {
  if (isMockedMode.value) {
    return true;
  }

  const customisationLsKey = 'customisation-enabled';
  const customisationLsValue = localStorage.getItem(customisationLsKey);
  return customisationLsValue === 'true';
});

const hideWidgetPages = ['/conversation/'];

const isHideWidgetPage = computed<boolean>(() => {
  return hideWidgetPages.some((page) => route.path.startsWith(page));
});

const isNewChatbotFlowEnabled = computed<boolean>(() => {
  if (!env.get('features')?.customisation) return false;
  if (isLocalStorageCustomisationEnabled.value) return true;
  if (isNewChatbotFlowBypassed(vuexStore.state.user.pleasedUser?.initForeignId)) return true;

  const country = env.get('country') as Country;
  const abPercentage = env.get('customisationAbPercentage')?.[country] ?? 0;
  return checkCustomisationABTestingPassed({
    // Static user id is used, as we need to know type of UI – old/new before we have user id data
    // And UI should not change on login/logout
    userId: 'user',
    abPercentage,
    checkBypass: false,
  });
});

/* For now we will allow new chatbot only for users who passed all of the following:
 * - Customisation feature enabled per environment
 * - Local storage customisation enabled (later change to A/B testing with % of users)
 */
const showChatWidget = computed<boolean>(() => {
  return Boolean(isNewChatbotFlowEnabled.value && !isHideWidgetPage.value);
});

const showCustomisationLoader = computed(() => {
  if (!isNewChatbotFlowEnabled.value) {
    return false;
  }

  if (!isInitialized.value || isLoadingCustomisation.value) {
    return true;
  }

  // Show loader until we have documents in the store and user is logged in
  return false;
});

/** Host iframe is ~43×43 (wap) / 48×48 (web) in float-bubble mode; match FloatButton icon (~34 / 45px). */
const floatBubbleLoaderSpinnerPx = computed<number | undefined>(() => {
  if (!showFloatButton.value) {
    return undefined;
  }
  return platform === 'wap' ? 38 : 45;
});

const fillPayloadWithFallbackAndDefaults = (payload: InitializeOptions) => {
  return {
    ...payload,
    selectedEntity: isValidSelectedEntity(payload.selectedEntity) ? payload.selectedEntity : undefined,
    pleasedUrl: payload.pleasedUrl || env.get('url'),
    brandCode: payload.brandCode || env.get('brandCode'),
    appName: env.get('appName'),
    firebaseConfig: {
      auth: env.get('firebase').auth,
      database: env.get('firebase').database,
      projectId: env.get('firebase').projectId,
    },
  };
};

// Methods
const initProject = async (payload: InitializeOptions) => {
  const unmountWidgetRoot = (errorMessage: string): void => {
    logger.error('[PLEASED_APP] Unmounting widget root because of error', { error: errorMessage });
    if (!window.VMPY) return;
    window.VMPY.$destroy();
    window.VMPY.$el?.parentNode?.removeChild(window.VMPY.$el);
  };

  try {
    // TODO: Establish one place for initial config setup

    // Init project for the old (vuex) store and old chatbot
    // TODO: remove/move logic parts from it after migration to new UI
    vuexStore.commit('user/initProject', payload);

    // IMPORTANT:
    // In the initProject we init env, so no sense to use it before it is initialized
    const payloadWithFallback = fillPayloadWithFallbackAndDefaults(payload);

    // Brand is known after env/config fallbacks; guest status is refined after auth resolution.
    // Route is kept in sync via bindChatbotRouteTracking; set once here for early bootstrap errors.
    setChatbotContext({
      brand: payloadWithFallback.brandCode,
      isGuest: !isAuthenticated.value,
      route: resolveTrackingRoute(route),
      clientId: payloadWithFallback.pleasedUrl.replace('https://', '').replace('http://', ''),
      loader: 'legacy',
      appName: payloadWithFallback.appName,
    });

    // Config setup for PleasedClient and configStore, new overhaul UI
    configStore.setConfig(payloadWithFallback);
    PleasedClient.setConfig({
      url: payloadWithFallback.pleasedUrl,
      brandCode: payloadWithFallback.brandCode,
      token: payloadWithFallback.sporty?.token || payloadWithFallback.token,
      acceptLanguage: payloadWithFallback.defaultLanguage as SupportedLanguages,
    });

    // Set mocked customisation documents if provided via INIT
    if (payload.customisation?.length || payload.initialRegistryId) {
      customisationStore.setMockedDocuments(payload.customisation || []);
    }

    // Execute mocked flow immediately if initialRegistryId is provided
    // This must happen before ChatWidget renders to set up the correct UI state
    if (payload.initialRegistryId) {
      widgetConversation.resetFlow();
      mockedFlow.executeMockedFlow();
      widgetConversation.startFlow();
    }

    const supportJweToken = !!payload.supportJweToken;

    if (isNewChatbotFlowEnabled.value) {
      isCustomisationEnabled.value = true;

      // Skip JWE / legacy token resolution when already authenticated or widget login is in flight (LoginForm handles it).
      const skipInitAuthResolution = isAuthenticated.value || userStore.bridgeWidgetLoginInProgress;

      if (!skipInitAuthResolution) {
        if (supportJweToken) {
          isAwaitingJweResolution.value = true;
          try {
            await ensurePleasedUserToken();
            const pleasedUser = vuexStore.state.user.pleasedUser;
            if (pleasedUser?.token && !pleasedUser?.isGuest) {
              userStore.setCurrentUser(pleasedUser as Parameters<typeof userStore.setCurrentUser>[0]);
              PleasedClient.setConfig({ token: pleasedUser.token });
            }
          } catch (error) {
            logErrorEvents({
              event: 'ensurePleasedUserToken',
              message: `INIT: ${(error as Error)?.message || String(error)}`,
            });
            captureEventException(error, ErrorTrackingEvent.WidgetInitAuth);
          } finally {
            isAwaitingJweResolution.value = false;
          }
        } else if (vuexStore.state.user.token) {
          await loginViaToken({
            token: vuexStore.state.user.token,
            countryCode: vuexStore.state.user.country,
          });
        }
      }

      setChatbotContext({
        isGuest: !isAuthenticated.value,
        route: resolveTrackingRoute(route),
      });

      if (route.path === '/') {
        // replace so legacy Chatroom at `/` is not left in history behind new-home
        router.replace({ name: 'new-home' });
      }
    }

    // Set flow as initialized, but don't show router for WEB flow
    isInitialized.value = true;
    if (platform === 'wap' || platform === 'web') {
      if (payload.open) {
        // For INIT event like Sportybet web in home page, only when open Chatroom/index.vue when it is opened, to reduce API call (ex: /user/createUser, /category/list)
        showRouterView.value = true;
      }
    } else {
      // natvie
      showRouterView.value = true;
    }
  } catch (error) {
    logger.error('[PLEASED_APP] Failed to init project', {
      error: parseErrorMessage(error),
    });
    captureEventException(error, ErrorTrackingEvent.WidgetInit);
    unmountWidgetRoot('Failed to init project');
  }
};

const ensurePleasedUserToken = () => {
  return vuexStore.dispatch('user/ensurePleasedUserToken');
};

const updateSelectedEntity = (payload: SelectedEntity | null) => {
  vuexStore.commit('user/updateSelectedEntity', payload);
};

/**
 * Handle real-time customisation updates from parent (CUSTOMISATION_UPDATE event).
 * Patches mocked documents and/or updates config (e.g. initialRegistryId) and re-runs mocked flow when needed.
 */
const handleCustomisationUpdate = (data: CustomisationUpdatePayload) => {
  if (data.customisation?.length) {
    customisationStore.patchMockedDocuments(data.customisation);
  }
  if (Object.prototype.hasOwnProperty.call(data, 'initialRegistryId')) {
    configStore.setConfig({ initialRegistryId: data.initialRegistryId });
    if (data.initialRegistryId != null) {
      widgetConversation.resetFlow();
      mockedFlow.executeMockedFlow();
      widgetConversation.startFlow();
    } else {
      mockedFlow.hideOverlayAndResetMockedFlow();
    }
  }
};

const handleCustomisationRetry = () => {
  refetch();
};

// Watchers
watch(showRouterView, (newVal) => {
  if (isMockedMode.value) {
    return;
  }

  // Analytics tracking
  if (newVal) {
    const entryLog = getEntryLog(pageSource.value);

    $pr.chatBotOpened();
    analytics.startFlushingLogs();
    analytics.track({
      name: GENERAL_EVENTS.CHATBOT_OPENED,
      metadata: {
        source: entryLog.entryType,
        url: entryLog.url,
      },
    });
  }
});

// Lifecycle - onMounted replaces created
onMounted(() => {
  // Interaction
  registerEventFromApp(); // Need to only call once before listenFromParent, it only used for App(handled internally)

  // Listen
  const onLogin = async (data: LoginResult) => {
    if (Object.prototype.hasOwnProperty.call(data, 'bizCode') && data.bizCode !== 10000) {
      return;
    }
    if (userStore.bridgeWidgetLoginInProgress) {
      return;
    }
    await vuexStore.dispatch('user/sportyLoginSuccess', data).catch((error: unknown) => {
      captureEventException(error, ErrorTrackingEvent.WidgetAuth);
    });
    if (!isNewChatbotFlowEnabled.value) {
      return;
    }
    const pleasedUser = vuexStore.state.user.pleasedUser;
    const hasLoggedInPleasedProfile = !!pleasedUser && !pleasedUser.isGuest && !!(pleasedUser.id || pleasedUser.token);
    if (hasLoggedInPleasedProfile) {
      userStore.setCurrentUser(pleasedUser as Parameters<typeof userStore.setCurrentUser>[0]);
      if (pleasedUser.token) {
        PleasedClient.setConfig({ token: pleasedUser.token });
      }
    }
  };

  Bridge.listenFromParent('LOGIN', onLogin);
  Bridge.listenFromParent('LOGIN_POP', onLogin);

  Bridge.listenFromParent<ParentEvDataOpenRes>('OPEN', async (data) => {
    // Currently we don't receive OPEN event on chatbot reopening, this is flow is to be revised
    // Due to that, we don't reset selected entity on chatbot minimize/go back, which results in undesired (showing prev flow)
    updateSelectedEntity(data.selectedEntity);
    emitFloatButton(false);
    Bridge.sendToParent('MINIMIZE', {
      period: MINIMIZE_TIME, // sec
      open: true,
    });

    const chatStore = (vuexStore.state as { chat: { isInitialized: boolean } }).chat;

    // If already initialized, then we need to send to redirect page
    if (chatStore.isInitialized) {
      Bridge.redirectToLastEventPage();
    }
  });

  Bridge.listenFromParent<InitializeOptions>('INIT', async (data) => {
    // Logged before initProject so a payload that breaks initialisation is still visible in dev logs.
    void logWidgetInitEvent(data);

    const { open } = data;
    if (!showRouterView.value && !open) {
      // For INIT event like Sportybet web in home page, should also do minimize
      emitFloatButton(true);
    }
    await initProject({
      ...data,
      provider: CustomerServiceProvider.PLEASED,
    });
  });

  Bridge.listenFromParent<CustomisationUpdatePayload>('CUSTOMISATION_UPDATE', handleCustomisationUpdate);

  // listenFromParent('GO_LIVE_CHAT', (data) => {
  //   this.$router.push({ name: 'chatroom' });
  // });
  Bridge.sendToParent('INIT'); // Bridge-INIT-Send

  // For dev test, uncomment all lines until onFloatButton. You can also use demo.html to serve it
  // initProject({
  //   country: Country.NG,
  //   token:
  //     'patron:id:accesstoken:cedef538c66dcb7544eee0dc92ab206cWjeTS9csWAcI4S3xl+qBewWHyvHvtKb0ieo4O7NGkNblF4+l99XQ8LGz7enYeBJAk3jz6vcSb+hhPL6wuDov7g==',
  //   chatStatus: '',
  //   provider: CustomerServiceProvider.PLEASED,
  //   phone: '',
  //   email: '',
  //   open: true,
  //   sporty: { country: Country.NG, currency: 'NGN' },
  //   // supportJweToken: true,
  //   userId: '7771110000',
  //   metaTags: { country: Country.NG },
  //   pleasedUrl: 'https://stage.pleased2.com',
  //   brandCode: 'sportybetng-uat',
  //   appName: 'SportyBet',
  //   firebaseConfig: {
  //     auth: 'pleased-development.firebaseapp.com',
  //     database: 'https://pleased-development.firebaseio.com',
  //     projectId: 'pleased-development',
  //   },
  // });
  // // 7771110000 test1234ng

  onFloatButton((isOpen) => {
    showFloatButton.value = isOpen && (platform === 'wap' || platform === 'web'); // Only mobile web and desktop can use FloatButton
    if (isOpen === false) {
      showRouterView.value = true;

      const chatStore = (vuexStore.state as { chat: { isInitialized: boolean } }).chat;

      // If already initialized, then we need to send to redirect page
      if (chatStore.isInitialized) {
        Bridge.redirectToLastEventPage();
      }
    }
  });

  onOpenMyRequest((isOpen) => {
    showMyRequest.value = isOpen;
  });

  onOpenArticle((data) => {
    showArticle.value = data.isOpen;
    showArticleId.value = data.articleId || '';
    articleTitle.value = data.title || '';
    categoryName.value = data.categoryName || '';
    articleBody.value = data.body || '';
    articleSectionTitle.value = data.sectionTitle || '';
    fetchArticle.value = data.fetchArticle || false;
    isFromAutocomplete.value = data.isFromAutocomplete || false;
    articleUseConfig.value = data.useConfig || null;
  });

  window.addEventListener('error', (error) => {
    logErrorEvents({ event: 'Window Error', message: error.message });
  });

  document.addEventListener(
    'error',
    (error) => {
      logErrorEvents({ event: 'Document Error', message: error.message });
    },
    true
  );
});
</script>

<style lang="scss">
@import '@/assets/scss/common.scss';

.app-content {
  height: 100vh;
}

// Mocked mode styles - blocks all interactions
.mocked-mode-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  cursor: not-allowed;
  // Transparent overlay that captures all clicks
  background: transparent;
}

// Optional: Add visual indication that interactions are disabled
#app.mocked-mode {
  // Disable text selection in mocked mode
  user-select: none;
}
</style>
